// Typed fetch client for the Flowio Worker API (/api/*).
// In dev, Vite proxies /api to `wrangler dev`; in prod the same Worker serves it.
// Auth is the Google session cookie (flowio_session), sent automatically same-origin.

export type ApiError = { error: string; how?: string };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith("/api/") || path === "/api" ? path : `/api${path}`;
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  const res = await fetch(url, { ...init, method, headers, credentials: "same-origin" });
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) message = body.how ? `${body.error} — ${body.how}` : body.error;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  request,
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ---- Shared response types (kept in sync with worker/routes) ----

export type HealthResponse = { ok: boolean; service: string; time: string };

export type ActivityRow = {
  id: number;
  person: string;
  metric: string;
  value: number;
  occurred_on: string;
};

export type TrackingResponse = {
  kind: "setter" | "sales";
  rows: ActivityRow[];
};
